import axios from 'axios';
import toast from 'react-hot-toast';

import { api as config } from '../../global/config/api';
import { ApiError } from './error';
import { broadcastLogout, readCsrfCookie } from './session';

// TSS vite/04 — the canonical HTTP client. Layer 1.
// All app code goes through this instance (directly or via useQuery / useBridge).

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

export const api = axios.create({
    baseURL: config.endpoint,
    timeout: 15_000,
    withCredentials: true,
});

api.interceptors.request.use((cfg) => {
    inferContentType(cfg);

    if (MUTATING.has(String(cfg.method).toLowerCase())) {
        const csrf = readCsrfCookie();
        if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
    }

    cfg.metadata = { id: crypto.randomUUID(), start: performance.now() };
    cfg.headers['X-Request-ID'] = cfg.metadata.id;

    return cfg;
});

api.interceptors.response.use(
    (res) => {
        if (import.meta.env.DEV) {
            const ms = (performance.now() - res.config.metadata.start).toFixed(0);
            console.debug(`[api] ${res.config.method?.toUpperCase()} ${res.config.url} → ${res.status} (${ms}ms)`);
        }
        return res;
    },
    async (error) => {
        // Blob responseType (e.g. PDF download) makes error bodies arrive as a
        // Blob too — decode to JSON so ApiError can read { error, fields }.
        if (error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                error.response.data = text ? JSON.parse(text) : null;
            } catch { /* not JSON — leave the Blob, message falls back to `Error N` */ }
        }

        const apiError = ApiError.fromAxios(error);

        if (apiError.status === 401) handle401(error);
        if (apiError.status === 403) handle403();

        throw apiError;
    },
);

// TSS vite/04 §"Content-type auto-detection". Sniffs cfg.data and sets
// (or removes) the Content-Type header. Caller never sets it manually.
function inferContentType(cfg) {
    if (cfg.data == null) return;
    if (cfg.headers['Content-Type']) return;

    if (cfg.data instanceof FormData) {
        // Browser must set the multipart boundary; pre-setting breaks it.
        delete cfg.headers['Content-Type'];
        return;
    }
    if (cfg.data instanceof Blob) {
        cfg.headers['Content-Type'] = cfg.data.type || 'application/octet-stream';
        return;
    }
    if (cfg.data instanceof URLSearchParams) {
        cfg.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        return;
    }
    if (cfg.data instanceof ArrayBuffer || ArrayBuffer.isView(cfg.data)) {
        cfg.headers['Content-Type'] = 'application/octet-stream';
        return;
    }
    if (typeof cfg.data === 'object') {
        cfg.headers['Content-Type'] = 'application/json';
    }
}

// Per TSS vite/05 §"Global error side effects" — only 401 and 403
// trigger global behavior. Everything else is the caller's job.

function handle401(error) {
    const url = error?.config?.url || '';
    // auth/* requests bypass — otherwise /auth/me returning 401 hard-reloads to
    // /login, which bootstraps /auth/me again → infinite reload loop. Requests
    // use relative urls ('auth/me'), so match with or without a leading slash.
    if (/(^|\/)auth\//.test(url)) return;
    broadcastLogout();
    // AuthProvider listens for 'logout_signal' storage events and clears state.
    window.location.href = '/login';
}

let forbiddenToastActive = false;
function handle403() {
    if (forbiddenToastActive) return;
    forbiddenToastActive = true;
    toast.error('No tenés permiso para esta acción');
    setTimeout(() => { forbiddenToastActive = false; }, 2000);
    window.dispatchEvent(new CustomEvent('auth:permissions-refresh'));
}

// Convenience helpers. TSS vite/04 §"File uploads" / §"File downloads".

export async function uploadFile(endpoint, file, fields = {}, opts = {}) {
    const { onProgress, signal, fileField = 'file' } = opts;
    const form = new FormData();
    form.append(fileField, file);
    for (const [k, v] of Object.entries(fields)) form.append(k, String(v));
    const res = await api.post(endpoint, form, {
        onUploadProgress: onProgress,
        signal,
    });
    return res.data;
}

export async function downloadFile(endpoint, opts = {}) {
    const res = await api.get(endpoint, {
        responseType: 'blob',
        signal: opts.signal,
    });
    return res.data;
}
